import React from "react";
import ReactSelect, {
  components,
  type OptionProps,
  type SingleValueProps,
  type StylesConfig,
} from "react-select";

import LeadSenderAvatar from "./LeadSenderAvatar";

export type AccountSelectOption = {
  value: string;
  label: string;
  avatar?: string;
};

type AccountSelectProps = {
  label?: string;
  value?: string;
  options: AccountSelectOption[];
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
};

const selectStyles: StylesConfig<AccountSelectOption, false> = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
    borderColor: state.isFocused ? "var(--color-brand-blue)" : "#E1E1E1",
    boxShadow: "none",
    fontSize: 14,
    "&:hover": {
      borderColor: state.isFocused ? "var(--color-brand-blue)" : "#E1E1E1",
    },
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: 36,
    padding: "0 10px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#EEF2F5" : state.isFocused ? "#F8FAFC" : "#FFFFFF",
    color: "var(--color-dark-black)",
    cursor: "pointer",
    fontSize: 14,
    paddingTop: 7,
    paddingBottom: 7,
    paddingLeft: 10,
    paddingRight: 10,
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: 8,
    border: "1px solid #E1E1E1",
    boxShadow: "0 8px 20px rgba(17, 15, 42, 0.08)",
    overflow: "hidden",
    zIndex: 30,
    marginTop: 6,
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "var(--color-dark-grey)",
    paddingTop: 0,
    paddingBottom: 0,
    paddingRight: 10,
    "&:hover": {
      color: "var(--color-dark-grey)",
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "var(--color-dark-black)",
    fontSize: 14,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "var(--color-dark-grey)",
    fontSize: 14,
  }),
};

const AccountOption = (props: OptionProps<AccountSelectOption, false>) => {
  const { data } = props;
  return (
    <components.Option {...props}>
      <div className="flex items-center gap-2">
        <LeadSenderAvatar picture={data.avatar} className="h-5 w-5" />
        <span className="truncate text-sm text-dark-black">{data.label}</span>
      </div>
    </components.Option>
  );
};

const AccountSingleValue = (props: SingleValueProps<AccountSelectOption, false>) => {
  const { data } = props;
  return (
    <components.SingleValue {...props}>
      <div className="flex items-center gap-2">
        <LeadSenderAvatar picture={data.avatar} className="h-5 w-5" />
        <span className="truncate text-sm text-dark-black">{data.label}</span>
      </div>
    </components.SingleValue>
  );
};

export default function AccountSelect({
  label,
  value,
  options,
  onChange,
  placeholder = "Select account",
  disabled = false,
}: AccountSelectProps) {
  const selectedOption =
    options.find((option) => option.value === value) ||
    (options.length > 0 ? options[0] : null);

  return (
    <label className="block space-y-2 text-sm font-medium text-dark-black">
      {label ? <span>{label}</span> : null}
      <ReactSelect<AccountSelectOption, false>
        isSearchable={false}
        isDisabled={disabled}
        options={options}
        value={selectedOption}
        placeholder={placeholder}
        onChange={(next) => onChange?.(next?.value || "")}
        styles={selectStyles}
        components={{
          Option: AccountOption,
          SingleValue: AccountSingleValue,
        }}
      />
    </label>
  );
}

